import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as guardianApi from '@/features/guardian/api';
import type {
  GuardianAccessScope,
  GuardianStudentView,
  InviteGuardianInput,
  InviteGuardianResult,
  StudentGuardianView,
} from '@/features/guardian/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/**
 * Tenant-scoped query keys embed the Active Organization id (ADR-026
 * Addendum, AO-9) - Org A's cached Guardian data cannot structurally
 * satisfy an Org B query, independent of the broader tenant cache clear
 * `ActiveOrganizationProvider` also performs on switch/logout.
 */
const studentGuardiansKey = (
  organizationId: string | null,
  studentId: string,
): readonly unknown[] => [TENANT_QUERY_PREFIX, organizationId, 'guardians', 'student', studentId];

const myGuardianStudentsKey = (organizationId: string | null): readonly unknown[] => [
  TENANT_QUERY_PREFIX,
  organizationId,
  'guardians',
  'me',
];

export function useStudentGuardians(
  studentId: string,
): UseQueryResult<readonly StudentGuardianView[], ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: studentGuardiansKey(activeOrganizationId, studentId),
    queryFn: () => guardianApi.listStudentGuardians(studentId),
    enabled: activeOrganizationId !== null,
  });
}

export function useInviteGuardian(
  studentId: string,
): UseMutationResult<InviteGuardianResult, ApiError, InviteGuardianInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: InviteGuardianInput) => guardianApi.inviteGuardian(studentId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: studentGuardiansKey(activeOrganizationId, studentId),
      });
    },
  });
}

/**
 * `revokeGuardian` serves both the administrative path (`students:manage`
 * revoking someone else's link) and self-revoke (a Guardian revoking their
 * own) - `guardian.controller.ts` resolves which one from the caller at
 * request time (Phase G-1's doc comment on `GuardianSection`). Either way,
 * the caller's own "Anak Saya" list (`useMyGuardianStudents`) can be
 * showing the just-revoked link: self-revoke always affects the caller's
 * own list, and the production `queryClient` holds a 30s `staleTime`
 * (`app/query-client.ts`), so without this invalidation a Guardian who
 * revokes their own access and returns to Anak Saya could still see the
 * revoked child for up to 30 seconds (Phase G-5, Job 15).
 */
export function useRevokeGuardian(studentId: string): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (guardianId: string) => guardianApi.revokeGuardian(studentId, guardianId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: studentGuardiansKey(activeOrganizationId, studentId),
      });
      void queryClient.invalidateQueries({
        queryKey: myGuardianStudentsKey(activeOrganizationId),
      });
    },
  });
}

export function useMyGuardianStudents(): UseQueryResult<readonly GuardianStudentView[], ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: myGuardianStudentsKey(activeOrganizationId),
    queryFn: () => guardianApi.listMyGuardianStudents(),
    enabled: activeOrganizationId !== null,
  });
}

/**
 * The caller's own `accessScope` for `studentId`, if any (Phase G-2, Job
 * 18). Used to disambiguate a `null` field that is genuinely optional at
 * write time (e.g. `MurajaahSession.notes`) from one the backend redacted
 * for LIMITED_READ - unlike Evaluation's rubric scores, which are always
 * required and so unambiguously signal redaction on their own, a Murajaah
 * session's `notes` field can legitimately be `null` for every caller.
 * `accessLevel` is `null` when the current viewer holds no ACTIVE Guardian
 * link to this student at all (Teacher/Admin/Self never redacted, so
 * `null` there always means "genuinely absent," never "restricted") - but
 * also, indistinguishably, while the underlying list is still loading or
 * failed to load. `isLoading`/`isError` (Phase G-5, Job 13) let a caller
 * tell those cases apart: a consumer that would otherwise render an
 * ambiguous `null` field as "no notes" must instead wait for both to
 * clear, so a LIMITED_READ Guardian never sees a briefly-mislabeled
 * "absent" during the query's own first fetch or a transient failure.
 */
export function useGuardianAccessLevelForStudent(studentId: string): {
  readonly accessLevel: GuardianAccessScope | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
} {
  const { data, isLoading, isError } = useMyGuardianStudents();
  const link = data?.find((entry) => entry.studentId === studentId);
  return { accessLevel: link?.accessScope ?? null, isLoading, isError };
}

/**
 * Not tenant-scoped (AO-12) - acceptance works with no Active
 * Organization selected, so this mutation does not read one. On success
 * it invalidates every organization's cached "my students" list, since
 * AO-13 re-resolves membership via `/auth/me` rather than assuming which
 * organization was just granted.
 */
export function useAcceptGuardianInvitation(): UseMutationResult<
  StudentGuardianView,
  ApiError,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => guardianApi.acceptGuardianInvitation(token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [TENANT_QUERY_PREFIX] });
    },
  });
}
